import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../Layout/MainLayout";
import classes from "./edit-member.module.css";
import { env } from "../../constants/env";
import { FiPauseCircle, FiPlayCircle } from "react-icons/fi";
import toast from "react-hot-toast";

const groupOptions = [
  { value: "A-DFY", label: "A-DFY" },
  { value: "A-DFY1", label: "A-DFY1" },
  { value: "A-DFYF", label: "A-DFYF" },
];

const accountTypeOptionsByCompany = {
  pow: [
    {
      value: "Instant Funding (Standard)",
      label: "Instant Funding (Standard)",
    },
    {
      value: "Instant Funding (Starter Plan)",
      label: "Instant Funding (Starter Plan)",
    },
    { value: "Instant Funding (DFY)", label: "Instant Funding (DFY)" },
  ],
  "Archangel Funding": [
    {
      value: "Instant Funding (Standard)",
      label: "Instant Funding (Standard)",
    },
    {
      value: "Instant Funding (Starter Plan)",
      label: "Instant Funding (Starter Plan)",
    },
  ],
  "DealFlow Partner": [
    {
      value: "Instant Funding (Standard)",
      label: "Instant Funding (Standard)",
    },
    {
      value: "Instant Funding (Starter Plan)",
      label: "Instant Funding (Starter Plan)",
    },
    { value: "Instant Funding (DFY)", label: "Instant Funding (DFY)" },
  ],
};

const defaultParametersByPlan = {
  "Instant Funding (Standard)": {
    maxDrawdown: 8,
    dailyLoss: "",
    drawdownType: "",
  },
  "Instant Funding (Starter Plan)": {
    maxDrawdown: "",
    dailyLoss: 3,
    drawdownType: "",
  },
  "Instant Funding (DFY)": {
    maxDrawdown: 7.1,
    dailyLoss: "",
    drawdownType: "new",
  },
};

const Dropdown = ({
  selectedOption,
  setSelectedOption,
  options,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={classes.dropdownWrapper} ref={dropdownRef}>
      <div className={classes.dropdownCard} onClick={() => setIsOpen(!isOpen)}>
        {/* Fall back to the raw saved value when it isn't in the options list
            (e.g. options not loaded yet) instead of rendering blank. */}
        {selectedOption !== null &&
        selectedOption !== undefined &&
        selectedOption !== ""
          ? (options.find((opt) => opt.value === selectedOption)?.label ??
            String(selectedOption))
          : placeholder || "Select an option"}
      </div>

      {isOpen && (
        <div className={classes.dropdownOptions}>
          {options.map((option) => (
            <div
              key={option.value}
              className={classes.dropdownOption}
              onClick={() => {
                setSelectedOption(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyAccount = {
  accountName: "",
  credentials: {
    accountNumber: "",
    masterPassword: "",
    investorPassword: "",
    server: "",
    depositCorrection: "",
    accountType: "",
    apiId: "",
    realizedCorrection: "",
    cumulativeCorrection: "",
  },
};

const EditCompanyMember = () => {
  const { id } = useParams();
  const { companyId } = useParams();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [accessLevel, setAccessLevel] = useState([]);
  const [overviewOn, setOverviewOn] = useState(false);
  const [cockpitOn, setCockpitOn] = useState(false);
  const [whitelabelOn, setWhitelabelOn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");

  const navigate = useNavigate();

  const [isSending, setIsSending] = useState(false);

  const accountTypeOptions = accountTypeOptionsByCompany[name] || [];
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(null);

  // Masters POW Director added (trade_sync_account_type: 'master'), for the
  // Tradesync copier dropdown.
  const [masters, setMasters] = useState([]);
  const [selectedMasterId, setSelectedMasterId] = useState(null);
  // null | "connect" | "disconnect" — drives the Tradesync button spinner label.
  const [tradesyncBusy, setTradesyncBusy] = useState(null);
  // Whether the copier should copy the master's already-open trades on connect.
  const [copyExisting, setCopyExisting] = useState(false);

  const selectedAccount =
    selectedAccountIndex !== null ? accounts[selectedAccountIndex] : null;

  // Account type is derived from the selected account (single source of truth).
  // It used to be separate state, which raced with a legacy fetch and could
  // display a stale/wrong value — and it drives the Parameters section below.
  const accountType = selectedAccount?.credentials?.accountType || "";

  // Tradesync (copier) state derived from the account's persisted fields:
  //   copier_id present  -> connected (following a master)
  //   follower_id only   -> connecting (TradeSync account made, copier pending)
  //   neither            -> available
  const tradesyncStatusOf = (acc) => {
    if (!acc) return "none";
    if (acc.copier_id) return "connected";
    if (acc.follower_id) return "connecting";
    return "available";
  };
  const tsStatus = tradesyncStatusOf(selectedAccount);

  useEffect(() => {
    if (accounts.length > 0 && selectedAccountIndex === null) {
      setSelectedAccountIndex(0);
    }
  }, [accounts, selectedAccountIndex]);

  // When the selected account changes, prefill the master it already follows
  // (parent_account_mongo_id -> `account`); reset for accounts not wired to a
  // master.
  useEffect(() => {
    if (selectedAccountIndex !== null && accounts[selectedAccountIndex]) {
      const acc = accounts[selectedAccountIndex];
      setSelectedMasterId(
        acc.copier_id || acc.follower_id ? acc.account || null : null,
      );
      setCopyExisting(false);
    }
  }, [selectedAccountIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getUser();
    getCompany();
    getMasters();
  }, []);

  const getMasters = async () => {
    try {
      const res = await fetch(`${env.primeBridgeUrl}/api/copier/masters`);
      if (!res.ok) throw new Error("Failed to fetch masters");
      const data = await res.json();
      setMasters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch masters", err);
    }
  };

  useEffect(() => {
    fetch(`${env.primeBridgeUrl}/api/company/themes`)
      .then((res) => res.json())
      .then((data) => {
        setThemes(data);
        const theme = data.find((t) => t._id === selectedTheme);
        const name = theme?.name; // safely get name
        setName(name);
      })
      .catch((err) => console.error(err));
  }, [selectedTheme]);

  const getUser = async () => {
    const sendRequest = async () => {
      const response = await fetch(
        `${env.primeBridgeUrl}/api/auth/user/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("sending plays failed.");
      }

      const result = await response.json();

      getAccounts(result._id);

      setFirstName(result?.firstName);
      setLastName(result?.lastName);
      setEmail(result?.email);
      setAccessLevel(result?.accessLevel);
      setIsAdmin(result?.isAdmin);

      console.log("USER EDIT RESULTS", result);

      return result;
    };

    try {
      await sendRequest();
    } catch (error) {
      console.log(error);
    }
  };

  const getAccounts = async (userId) => {
    try {
      const res = await fetch(
        `${env.primeBridgeUrl}/api/account/accounts/user/${userId}`,
      );
      if (!res.ok) throw new Error("Failed to fetch accounts");
      const data = await res.json();

      console.log("Fetched Accounts", data);

      const list = Array.isArray(data) ? data : [];
      setAccounts(list);
      setSelectedAccountIndex(list.length ? 0 : null);
    } catch (err) {
      console.error("Failed to fetch accounts", err);
    }
  };

  const updateUserHandler = async () => {
    let dataContent = {
      firstName: firstName,
      lastName: lastName,
      email: email,
      accessLevel: accessLevel,
      overview: overviewOn,
      cockpit: cockpitOn,
      whitelabel: whitelabelOn,
      theme: selectedTheme,
    };
    const sendRequest = async () => {
      if (whitelabelOn && !selectedTheme) {
        toast.error("Please select a theme for whitelabel");
        return;
      }
      setIsSending(true);
      const response = await fetch(
        `${env.primeBridgeUrl}/api/auth/update-user/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(dataContent),
        },
      );

      if (!response.ok) {
        setIsSending(false);
        console.log("ERROR RESPONSE", response.error);
        throw new Error("updating user failed.");
      }

      const result = await response.json();
      setIsSending(false);

      navigate(`/company/${companyId}`);

      console.log("Updated User", result);

      return result;
    };

    try {
      await sendRequest();
    } catch (error) {
      setIsSending(false);
      console.log(error);
    }
  };

  const getCompany = async () => {
    const sendRequest = async () => {
      const response = await fetch(
        `${env.primeBridgeUrl}/api/company/${companyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("sending plays failed.");
      }

      const result = await response.json();

      setOverviewOn(result.overview);
      setCockpitOn(result.cockpit);
      setWhitelabelOn(result.whitelabel);
      setSelectedTheme(result.theme._id);
      setCompanyName(result.companyName);

      console.log("Single Comapny data", result);

      return result;
    };

    try {
      await sendRequest();
    } catch (error) {
      console.log(error);
    }
  };

  const saveAccountHandler = async () => {
    if (selectedAccountIndex === null) return;

    const currentAccount = accounts[selectedAccountIndex];

    if (
      accountType === "Instant Funding (DFY)" &&
      !currentAccount.credentials?.group
    ) {
      toast.error("Group is required for Instant Funding (DFY) accounts");
      return;
    }

    const account = {
      ...currentAccount,
      credentials: {
        ...currentAccount.credentials,
        accountType,
      },
    };

    try {
      setIsSending(true);

      const res = await fetch(
        `${env.primeBridgeUrl}/api/account/accounts/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: id,
            account,
          }),
        },
      );

      if (!res.ok) throw new Error("Failed to save account");

      const savedAccount = await res.json();

      const updated = [...accounts];
      updated[selectedAccountIndex] = savedAccount;
      setAccounts(updated);

      toast.success("Account saved successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save account");
    } finally {
      setIsSending(false);
    }
  };

  const removeAccountHandler = async () => {
    if (selectedAccountIndex === null) return;

    const account = accounts[selectedAccountIndex];

    const confirm = window.confirm("Remove this account?");
    if (!confirm) return;

    try {
      setIsSending(true);

      if (account._id) {
        await fetch(
          `${env.primeBridgeUrl}/api/account/accounts/${account._id}`,
          {
            method: "DELETE",
          },
        );
      }

      const updated = accounts.filter((_, i) => i !== selectedAccountIndex);

      setAccounts(updated);

      if (updated.length === 0) {
        setSelectedAccountIndex(null);
      } else {
        setSelectedAccountIndex(Math.max(0, selectedAccountIndex - 1));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove account");
    } finally {
      setIsSending(false);
    }
  };

  const handleThemeChange = (event) => {
    const selectedTheme = themes.find(
      (theme) => theme._id === event.target.value,
    );
    setSelectedTheme(event.target.value);
    console.log("Selected Theme", selectedTheme);
  };

  const deployAccountHandler = async () => {
    let account = accounts[selectedAccountIndex];

    if (account.isDeployed) {
      // --- Undeploy ---
      try {
        setIsSending(true);
        const res = await fetch(
          `${env.primeBridgeUrl}/api/account/undeploy/${account._id}`,
          { method: "POST" },
        );
        if (!res.ok) throw new Error("Undeploy failed");

        const updated = [...accounts];
        updated[selectedAccountIndex].isDeployed = false;
        updated[selectedAccountIndex].credentials.sessionToken = null;
        setAccounts(updated);
        toast.success("Account undeployed");
      } catch (err) {
        toast.error("Failed to undeploy account");
        console.error(err);
      } finally {
        setIsSending(false);
      }
    } else {
      // --- Deploy ---
      try {
        setIsSending(true);

        // If new account (no _id), save it first to get an _id
        if (!account._id) {
          const saveRes = await fetch(
            `${env.primeBridgeUrl}/api/account/accounts/update`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId: id, account }),
            },
          );
          if (!saveRes.ok)
            throw new Error("Failed to save account before deploying");

          const savedAccount = await saveRes.json();
          const updated = [...accounts];
          updated[selectedAccountIndex] = savedAccount;
          setAccounts(updated);
          account = savedAccount; // use the saved account with _id
        }

        const deployRes = await fetch(
          `${env.primeBridgeUrl}/api/account/deploy/${account._id}`,
          { method: "POST" },
        );
        if (!deployRes.ok) throw new Error("Deploy failed");

        const { token } = await deployRes.json();
        const updated = [...accounts];
        updated[selectedAccountIndex].isDeployed = true;
        updated[selectedAccountIndex].credentials.sessionToken = token;
        setAccounts(updated);
        toast.success("Account deployed");
      } catch (err) {
        toast.error(err.message || "Failed to deploy account");
        console.error(err);
      } finally {
        setIsSending(false);
      }
    }
  };

  // Update the selected account's fields in place so the Tradesync button
  // reflects the new state immediately (no refetch needed).
  const patchSelectedAccount = (patch) => {
    setAccounts((prev) => {
      const updated = [...prev];
      updated[selectedAccountIndex] = {
        ...updated[selectedAccountIndex],
        ...patch,
      };
      return updated;
    });
  };

  // Toggle Tradesync for the selected account:
  //   connected            -> disconnect the copier
  //   available/connecting -> connect (or retry a pending connection)
  const tradesyncHandler = async () => {
    const account = accounts[selectedAccountIndex];
    if (!account || tradesyncBusy) return;

    if (tradesyncStatusOf(account) === "connected") {
      return disconnectTradesync(account);
    }
    return connectTradesync(account);
  };

  // Wire the selected account up as a slave/follower of the chosen master — the
  // same series of steps as the user portal's "add a slave" flow (create the
  // follower's TradeSync account, wait for it to connect, then link the copier).
  const connectTradesync = async (account) => {
    if (!selectedMasterId) {
      toast.error("Select a master account first");
      return;
    }
    if (!account._id) {
      toast.error("Save the account before running Tradesync");
      return;
    }

    try {
      setTradesyncBusy("connect");
      const res = await fetch(`${env.primeBridgeUrl}/api/copier/copiers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          masterId: selectedMasterId,
          slaveId: account._id,
          copyExisting: copyExisting ? "yes" : "no",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 202) {
        // Follower account created; copier not linked yet. Persist follower_id so
        // the button shows "Connecting…" and a click retries.
        patchSelectedAccount({
          follower_id: data.followerId,
          account: selectedMasterId,
        });
        toast(
          data.message ||
            "Account is still connecting to TradeSync. Try again shortly.",
        );
        return;
      }
      if (!res.ok) {
        throw new Error(data.error || "Tradesync failed");
      }

      patchSelectedAccount({
        copier_id: data.copierId,
        follower_id: data.followerId,
        account: data.masterId || selectedMasterId,
        tradesync_deployed: true,
      });
      // Deployed on the Standard risk level: the service picks the multiplier
      // from the account's deposit, so say which one it landed on.
      toast.success(
        `Now copying ${data.following || "master"}${data.mult != null ? ` at ${data.mult}x (Standard)` : ""}`,
      );
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Tradesync failed");
    } finally {
      setTradesyncBusy(null);
    }
  };

  // Unlink the copier and remove the follower's TradeSync account, then clear the
  // local state so the button returns to "Tradesync".
  const disconnectTradesync = async (account) => {
    try {
      setTradesyncBusy("disconnect");
      const res = await fetch(
        `${env.primeBridgeUrl}/api/copier/copiers/${account._id}`,
        { method: "DELETE" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to disconnect");

      // The copier is always gone at this point; the follower's own TradeSync
      // account may not be (TradeSync blocks deletes for ~2 min after an account
      // is created). Mirror the server: keep follower_id when it kept it, so the
      // account reads "Connecting…" until the background cleanup removes it —
      // the same state the user portal shows as "settingup".
      patchSelectedAccount({
        copier_id: null,
        follower_id: data.followerRemoved === false ? data.followerId : null,
        account: null,
        tradesync_deployed: false,
      });
      setSelectedMasterId(null);
      if (data.followerRemoved === false) {
        toast(
          "Disconnected from master. TradeSync is still holding the account; it will be removed shortly.",
        );
      } else {
        toast.success("Disconnected from master");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to disconnect");
    } finally {
      setTradesyncBusy(null);
    }
  };

  return (
    <MainLayout>
      <div className={classes.container}>
        <div className={classes.header}>
          <NavLink
            style={{ textDecoration: "none" }}
            to={`/company/${companyId}`}
          >
            <p className={classes.name}>{companyName}</p>
          </NavLink>
          <h3 className={classes.title}>Edit Member</h3>
        </div>
        <div className={classes.form}>
          <p className={classes.title}>Login Information</p>

          <div className={classes.group}>
            <div className={classes.inputContainer}>
              <input
                placeholder="First Name"
                className={classes.input}
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className={classes.inputContainer}>
              <input
                placeholder="Last Name"
                className={classes.input}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className={classes.inputContainer}>
            <input
              placeholder="Email"
              className={classes.input}
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="mt-5">
            <div className={classes.group}>
              <div className={classes.inputContainer}>
                <p className={classes.title}>MT5 Account</p>
              </div>
              <div className={classes.inputContainer}>
                <Dropdown
                  selectedOption={selectedAccountIndex}
                  setSelectedOption={setSelectedAccountIndex}
                  options={accounts.map((acc, index) => ({
                    value: index,
                    label: acc.accountName || `Account ${index + 1}`,
                  }))}
                  placeholder="Select Account"
                />
              </div>
            </div>
            <div className={classes.group}>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Account Name"
                  className={classes.input}
                  value={selectedAccount?.accountName || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[selectedAccountIndex].accountName = e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Account ID"
                  className={classes.input}
                  type="text"
                  value={selectedAccount?.credentials?.accountNumber || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[selectedAccountIndex].credentials.accountNumber =
                      e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
            </div>
            <div className={classes.group}>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Master PW"
                  className={classes.input}
                  type="text"
                  value={selectedAccount?.credentials?.masterPassword || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[selectedAccountIndex].credentials.masterPassword =
                      e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Investor PW"
                  className={classes.input}
                  type="text"
                  value={selectedAccount?.credentials?.investorPassword || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[selectedAccountIndex].credentials.investorPassword =
                      e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
            </div>
            <div className={classes.group}>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Server"
                  className={classes.input}
                  type="text"
                  value={selectedAccount?.credentials?.server || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[selectedAccountIndex].credentials.server =
                      e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Deposit"
                  className={classes.input}
                  type="text"
                  value={selectedAccount?.credentials?.depositCorrection || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[
                      selectedAccountIndex
                    ].credentials.depositCorrection = e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
            </div>
          </div>
          <div className={classes.inputContainer}>
            <Dropdown
              selectedOption={accountType}
              setSelectedOption={(value) => {
                if (selectedAccountIndex !== null) {
                  const updated = [...accounts];
                  updated[selectedAccountIndex].credentials = {
                    ...updated[selectedAccountIndex].credentials,
                    ...defaultParametersByPlan[value],
                    accountType: value,
                  };
                  setAccounts(updated);
                }
              }}
              options={accountTypeOptions}
              placeholder="Select Account Type"
            />
          </div>
          <div className={classes.inputContainer}>
            <Dropdown
              selectedOption={selectedMasterId}
              setSelectedOption={setSelectedMasterId}
              options={masters.map((m) => ({
                value: m.id,
                label: m.login ? `${m.name} (${m.login})` : m.name,
              }))}
              placeholder="Select Master (Tradesync)"
            />
          </div>
          <div className={classes.inputContainer}>
            <Dropdown
              selectedOption={selectedAccount?.credentials?.group || ""}
              setSelectedOption={(value) => {
                if (selectedAccountIndex !== null) {
                  const updated = [...accounts];
                  updated[selectedAccountIndex].credentials = {
                    ...updated[selectedAccountIndex].credentials,
                    group: value,
                  };
                  setAccounts(updated);
                }
              }}
              options={groupOptions}
              placeholder="Select Group"
            />
          </div>
          <div className={classes.inputContainer}>
            <input
              placeholder="Session Token"
              className={classes.input}
              type="text"
              value={selectedAccount?.credentials?.sessionToken || ""}
              onChange={(e) => {
                const updated = [...accounts];
                updated[selectedAccountIndex].credentials.sessionToken =
                  e.target.value;
                setAccounts(updated);
              }}
            />
          </div>
          <div className={classes.group}>
            <div className={classes.inputContainer}>
              <input
                placeholder="Realized Profit Correction"
                className={classes.input}
                type="text"
                value={selectedAccount?.credentials?.realizedCorrection || ""}
                onChange={(e) => {
                  const updated = [...accounts];
                  updated[selectedAccountIndex].credentials.realizedCorrection =
                    e.target.value;
                  setAccounts(updated);
                }}
              />
            </div>
            <div className={classes.inputContainer}>
              <input
                placeholder="Cumulative Profit Correction"
                className={classes.input}
                type="text"
                value={selectedAccount?.credentials?.cumulativeCorrection || ""}
                onChange={(e) => {
                  const updated = [...accounts];
                  updated[
                    selectedAccountIndex
                  ].credentials.cumulativeCorrection = e.target.value;
                  setAccounts(updated);
                }}
              />
            </div>
          </div>
          <p className={classes.title}>Parameters</p>
          {accountType !== "Instant Funding (Starter Plan)" && (
            <div className={classes.group}>
              <label>Max Drawdown %</label>
              <div className={classes.inputContainer}>
                <input
                  placeholder="Max Drawdown %"
                  className={classes.input}
                  type="text"
                  value={selectedAccount?.credentials?.maxDrawdown || ""}
                  onChange={(e) => {
                    const updated = [...accounts];
                    updated[selectedAccountIndex].credentials.maxDrawdown =
                      e.target.value;
                    setAccounts(updated);
                  }}
                />
              </div>
            </div>
          )}
          {accountType !== "Instant Funding (Standard)" &&
            accountType !== "Instant Funding (DFY)" && (
              <div className={classes.group}>
                <label>Daily Loss %</label>
                <div className={classes.inputContainer}>
                  <input
                    placeholder="Daily Loss %"
                    className={classes.input}
                    type="text"
                    value={selectedAccount?.credentials?.dailyLoss || ""}
                    onChange={(e) => {
                      const updated = [...accounts];
                      updated[selectedAccountIndex].credentials.dailyLoss =
                        e.target.value;
                      setAccounts(updated);
                    }}
                  />
                </div>
              </div>
            )}
          {accountType !== "Instant Funding (Starter Plan)" &&
            accountType !== "Instant Funding (Standard)" && (
              <div className={classes.group}>
                <label>Drawdown Type</label>
                <div className={classes.inputContainer}>
                  <Dropdown
                    selectedOption={
                      selectedAccount?.credentials?.drawdownType || ""
                    }
                    setSelectedOption={(value) => {
                      if (selectedAccountIndex !== null) {
                        const updated = [...accounts];
                        updated[selectedAccountIndex].credentials.drawdownType =
                          value;
                        setAccounts(updated);
                      }
                    }}
                    options={[
                      { value: "new", label: "New" },
                    ]}
                    placeholder="Select Drawdown Type"
                  />
                </div>
              </div>
            )}

          <label class={`${classes.check_cont}`}>
            <input
              type="checkbox"
              checked={copyExisting}
              onChange={(e) => setCopyExisting(e.target.checked)}
            />
            <span className={classes.checkmark}></span>
            <p>Copy existing lead account trades</p>
          </label>

          {isAdmin ? (
            <div className={classes.access}>
              <p className={classes.title}>User Access</p>
              <label class={`${classes.check_cont}`}>
                <input
                  type="checkbox"
                  checked={overviewOn}
                  onChange={(e) => setOverviewOn(!overviewOn)}
                />
                <span className={classes.checkmark}></span>
                <p>Overview section</p>
              </label>{" "}
              <label class={`${classes.check_cont}`}>
                <input
                  type="checkbox"
                  checked={cockpitOn}
                  onChange={() => setCockpitOn(!cockpitOn)}
                />
                <span className={classes.checkmark}></span>
                <p>Cockpit</p>
              </label>
              <label class={`${classes.check_cont}`}>
                <input
                  type="checkbox"
                  checked={whitelabelOn}
                  onChange={(e) => setWhitelabelOn(!whitelabelOn)}
                />
                <span className={classes.checkmark}></span>
                <p>Whitelabel</p>
              </label>
              {whitelabelOn && (
                <div>
                  <select
                    onChange={handleThemeChange}
                    style={{
                      padding: "8px 10px",
                      fontSize: "16px",
                      borderRadius: "100px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <option value="">Select a theme</option>
                    {themes.map((theme) => (
                      <option
                        key={theme._id}
                        value={theme._id}
                        selected={selectedTheme === theme._id}
                      >
                        {theme.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <div className={classes.access}>
              <div className={classes.actionRows}>
                {/* Row 1 — actions on the selected account */}
                <div className={classes.actionRow}>
                  <Link style={{ textDecoration: "none", display: "block" }}>
                    <button
                      className={classes.confirm}
                      onClick={saveAccountHandler}
                      disabled={selectedAccountIndex === null}
                    >
                      Save Account
                    </button>
                  </Link>
                  <Link style={{ textDecoration: "none", display: "block" }}>
                    <button
                      className={classes.deploy}
                      onClick={deployAccountHandler}
                    >
                      {selectedAccount?.isDeployed ? (
                        <>
                          <FiPauseCircle /> Undeploy
                        </>
                      ) : (
                        <>
                          <FiPlayCircle /> Tracking
                        </>
                      )}
                    </button>
                  </Link>
                  <Link style={{ textDecoration: "none", display: "block" }}>
                    <button
                      className={classes.tradesync}
                      onClick={tradesyncHandler}
                      disabled={selectedAccountIndex === null || !!tradesyncBusy}
                    >
                      {tradesyncBusy === "connect" ? (
                        "Connecting…"
                      ) : tradesyncBusy === "disconnect" ? (
                        "Disconnecting…"
                      ) : tsStatus === "connected" ? (
                        <>
                          <FiPauseCircle /> Disconnect
                        </>
                      ) : tsStatus === "connecting" ? (
                        "Connecting…"
                      ) : (
                        <>
                          <FiPlayCircle /> Tradesync
                        </>
                      )}
                    </button>
                  </Link>
                </div>

                {/* Row 2 — account lifecycle; Remove isolated on the right */}
                <div className={classes.actionRowSplit}>
                  <Link style={{ textDecoration: "none", display: "block" }}>
                    <button
                      className={classes.confirm}
                      onClick={() => {
                        setAccounts([
                          ...accounts,
                          structuredClone(emptyAccount),
                        ]);
                        setSelectedAccountIndex(accounts.length);
                      }}
                    >
                      New Account
                    </button>
                  </Link>
                  <Link style={{ textDecoration: "none", display: "block" }}>
                    <button
                      className={classes.delete}
                      onClick={removeAccountHandler}
                      disabled={selectedAccountIndex === null}
                    >
                      Remove
                    </button>
                  </Link>
                </div>
              </div>
              <p className={classes.title}>User Access</p>
              <div>
                <label class={classes.check_cont}>
                  <input
                    type="checkbox"
                    checked={accessLevel.includes("asset-dashboard")}
                    onChange={(e) => {
                      if (!accessLevel.includes("asset-dashboard")) {
                        setAccessLevel([...accessLevel, "asset-dashboard"]);
                      } else {
                        setAccessLevel((current) =>
                          current.filter(
                            (access) => access !== "asset-dashboard",
                          ),
                        );
                      }
                    }}
                  />
                  <span className={classes.checkmark}></span>
                  <p>Asset Dashboard</p>
                </label>
                <label class={classes.check_cont}>
                  <input
                    type="checkbox"
                    checked={accessLevel.includes("transactions")}
                    onChange={(e) => {
                      if (!accessLevel.includes("transactions")) {
                        setAccessLevel([...accessLevel, "transactions"]);
                      } else {
                        setAccessLevel((current) =>
                          current.filter((access) => access !== "transactions"),
                        );
                      }
                    }}
                  />
                  <span className={classes.checkmark}></span>
                  <p>Transactions</p>
                </label>
                <label class={classes.check_cont}>
                  <input
                    type="checkbox"
                    checked={accessLevel.includes("team")}
                    onChange={(e) => {
                      if (!accessLevel.includes("team")) {
                        setAccessLevel([...accessLevel, "team"]);
                      } else {
                        setAccessLevel((current) =>
                          current.filter((access) => access !== "team"),
                        );
                      }
                    }}
                  />
                  <span className={classes.checkmark}></span>
                  <p>Team</p>
                </label>
                <label class={classes.check_cont}>
                  <input
                    type="checkbox"
                    checked={accessLevel.includes("help-center")}
                    onChange={(e) => {
                      if (!accessLevel.includes("help-center")) {
                        setAccessLevel([...accessLevel, "help-center"]);
                      } else {
                        setAccessLevel((current) =>
                          current.filter((access) => access !== "help-center"),
                        );
                      }
                    }}
                  />
                  <span className={classes.checkmark}></span>
                  <p>Help Center</p>
                </label>
                {overviewOn && (
                  <label class={classes.check_cont}>
                    <input
                      type="checkbox"
                      checked={accessLevel.includes("overview")}
                      onChange={(e) => {
                        if (!accessLevel.includes("overview")) {
                          setAccessLevel([...accessLevel, "overview"]);
                        } else {
                          setAccessLevel((current) =>
                            current.filter((access) => access !== "overview"),
                          );
                        }
                      }}
                    />
                    <span className={classes.checkmark}></span>
                    <p>Overview section</p>
                  </label>
                )}
                {overviewOn && (
                  <label class={classes.check_cont}>
                    <input
                      type="checkbox"
                      checked={accessLevel.includes("cockpit")}
                      onChange={() => {
                        if (!accessLevel.includes("cockpit")) {
                          setAccessLevel([...accessLevel, "cockpit"]);
                        } else {
                          setAccessLevel((current) =>
                            current.filter((access) => access !== "cockpit"),
                          );
                        }
                      }}
                    />
                    <span className={classes.checkmark}></span>
                    <p>Cockpit</p>
                  </label>
                )}
              </div>
            </div>
          )}

          <div className={classes.btnContainer}>
            {!isSending && (
              <>
                <Link
                  style={{ textDecoration: "none", display: "block" }}
                  to={`/company/${companyId}`}
                >
                  <button className={classes.cancel}>Cancel</button>
                </Link>

                <Link style={{ textDecoration: "none", display: "block" }}>
                  <button
                    className={classes.confirm}
                    onClick={updateUserHandler}
                  >
                    Save Member
                  </button>
                </Link>
              </>
            )}
            {isSending && <div className="spinner-border" role="status"></div>}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditCompanyMember;
